import {
  mapMyAlbumAccess,
  mapMyVaultAccess,
} from 'networking/controllers/shares-controller';
import { vaultInvitationRole } from 'helpers/sharing';
import { memoriesOf } from 'helpers/memories';
/* eslint-disable max-len */
import { AgoraVideoCallProps } from 'models/agora-video-call-props';
import { Album } from 'models/album';
import { Owner } from 'models/owner';
import {
  VaultsQueryParamsRaw,
  VaultsQueryParamsSerialized,
} from 'networking/types/vaults-query-params';
import {
  AlbumContentRaw,
  AlbumContentSerialized,
  AlbumDetailRaw,
  AlbumDetailType,
} from 'networking/types/album-detail';
import { SharedAlbum, SharedAlbumRaw } from 'networking/types/shared-album';
import {
  CreateDocumentRequestRaw,
  DocumentRaw,
  DocumentType,
} from 'networking/types/documents';
import { AlbumLocationRaw } from 'networking/types/locations';
import { PaginationSerializer } from './pagination-serializer';
import { PaymentSerializer } from './payment-serializer';
import { OwnerSerializer } from './owner-serializer';

class VaultsSerializer {
  // TODO: Create album serializer, clip serializer, document serializer to remove all the logic from this serializer
  static deSerializeAlbumThumbnail = (data: AlbumThumbnailRaw) => ({
    blobId: data.blob_id,
    url: data.url,
  });

  static deSerializeAlbumCreatedByUser = (data: AlbumCreatedByUserRaw) => ({
    id: data.id,
    companyName: data.company_name,
    email: data.email,
    name: data.name,
  });

  static deSerializeAlbum = (
    data: AlbumRaw,
    globalOrderIndex: number,
  ): Album => ({
    id: data.id,
    batchId: data.batch_id,
    collections: data.collections,
    // Summary responses (with_albums=summary) carry no contents at all.
    contents: (data.contents ?? []).map((content) =>
      VaultsSerializer.albumDetailContentDeserializer(content),
    ),
    createdAt: data.created_at,
    createdBy: data.created_by,
    createdByContributor: data.created_by_contributor,
    createdByOwner: data.created_by_owner,
    createdByUser: data.created_by_user
      ? this.deSerializeAlbumCreatedByUser(data.created_by_user)
      : undefined,
    customThumbnailUrl: data.custom_thumbnail_url,
    endDate: data.end_date,
    isPrivate: data.is_private,
    isDefault: data.is_default ?? false,
    location: data.location,
    name: data.name,
    order: data.order,
    globalOrderIndex,
    startDate: data.start_date,
    thumbnail: this.deSerializeAlbumThumbnail(data.thumbnail),
    thumbnailUrl: data.thumbnail_url,
    totalSize: data.total_size,
    updatedAt: data.updated_at,
    guestInviteToken: data.guest_invite_token,
    guestTokenId: data.guest_token_id ?? null,
    myAccess: mapMyAlbumAccess(data.my_access),
    shareLinkEnabled: data.share_link_enabled,
  });

  static deSerializeDocument(data: DocumentRaw): DocumentType {
    return {
      id: data.content?.id || data.id,
      fileDate: data.file_date,
      fileSize: data.file_size,
      fileName: data.file_name,
      fileUrl: data.file_url,
      isImage: data.is_image,
      thumbnailUrl: data.thumbnail_url,
      uploaderId: data.uploader_id,
      private: data.private,
      vaultId: data.vault_id,
      albumId: data.album_id,
      createdAt: data.created_at,
      updatedAt: data.updated_at,
      contentType: data.content_type,
      isPrivate: data.private,
    };
  }

  static deSerializeJournalDocument(
    data: JournalDocumentRaw,
  ): JournalDocumentType {
    return {
      id: data.id,
      contentId: data.content.id,
      name: data.name,
      body: data.body,
      entryDate: data.entry_date,
      location: data.location,
      user: this.deSerializeAlbumCreatedByUser(data?.creator ?? {}),
      vaultId: data.vault_id,
      startDate: data.start_date,
      endDate: data.end_date,
      private: data.private,
      albumId: data.album_id,
    };
  }

  static deSerializeContributorInfo(
    data: ContributorRaw,
  ): ContributorSerialized {
    return {
      id: data.id || -1,
      name: data.name,
      email: data.email,
      backupEmail: data.backup_email,
      backupName: data.backup_name,
      invitationId: data.invitation_id,
      invitationStatus: data.invitation_status,
      invitationResent: false,
      contributions: data.contributed_vaults_and_clips?.length ?? 0,
      accessRole: data.access_role,
    };
  }

  static deSerializeGuestInfo(data: GuestRaw): GuestSerialized {
    return {
      id: data.id ?? -1,
      name: data.name,
      email: data.email,
      vaultId: data.vault_id,
      vaultTitle: data.vault_title,
      vaultUrl: data.vault_url,
      albumId: data.album_id,
      albumUrl: data.album_url,
      expirationDate: data.expiration_date,
      token: data.token,
      invitationStatus: data.invitation_status ?? 'accepted',
      contributions: data.contributed_vaults_and_clips?.length ?? 0,
      accessLevel: data.access_level,
      scope: data.scope,
    };
  }

  static albumDetailContentDeserializer = (
    raw: AlbumContentRaw,
  ): AlbumContentSerialized => ({
    id: raw.id,
    content: VaultsSerializer.deSerializeClip(raw.content),
    contentType: raw.content_type,
    createdAt: raw.created_at,
    updatedAt: raw.updated_at,
    createdByContributor: raw.created_by_contributor,
    createdByOwner: raw.created_by_owner,
    guestTokenId: raw.guest_token_id ?? null,
    myAccess: raw.my_access
      ? {
          canEdit: raw.my_access.can_edit,
          canDescribe: raw.my_access.can_describe,
          canSetPrivacy: raw.my_access.can_set_privacy,
          canShare: raw.my_access.can_share,
          mine: Boolean(raw.my_access.mine),
        }
      : undefined,
    user:
      raw.user || raw.owner
        ? {
            id: raw.owner?.id || raw.user?.id,
            name: raw.owner?.name || raw.user?.name,
            email: raw.user?.email || '',
            companyName: raw.user?.company_name || '',
          }
        : undefined,
    description: raw.description,
    fileDate: raw.file_date ? new Date(raw.file_date).toISOString() : null,
    fileSize: raw.file_size,
    isPrivate: raw.is_private,
    length: raw.length,
    mediaUrl: raw.media_url,
    name: raw.name,
    thumbnailUrl: raw.thumbnail_url,
    customThumbnailUrl: raw.custom_thumbnail_url ?? null,
    startDate: raw.start_date ?? undefined,
    endDate: raw.end_date ?? undefined,
    location: raw.location ?? null,
    qrCodes: (raw.qr_codes ?? []).map(VaultsSerializer.getDeSerializeQrCode),
    order: raw.order ?? 0,
  });

  static deSerializeAlbumDetail = (raw: AlbumDetailRaw): AlbumDetailType => ({
    id: raw.id,
    batchId: raw.batch_id,
    collections: raw.collections,
    contents: raw.contents
      .map(VaultsSerializer.albumDetailContentDeserializer)
      .reverse(),
    createdAt: raw.created_at,
    createdBy: raw.created_by,
    createdByContributor: raw.created_by_contributor,
    createdByOwner: raw.created_by_owner,
    guestTokenId: raw.guest_token_id ?? null,
    createdByUser: raw.created_by_user
      ? this.deSerializeAlbumCreatedByUser(raw.created_by_user)
      : undefined,
    customThumbnailUrl: raw.custom_thumbnail_url,
    endDate: raw.end_date,
    isPrivate: raw.is_private,
    location: raw.location,
    name: raw.name,
    order: raw.order,
    startDate: raw.start_date,
    thumbnail: {
      blobId: raw.thumbnail.blob_id,
      url: raw.thumbnail.url,
    },
    thumbnailUrl: raw.thumbnail_url,
    totalSize: raw.total_size,
    updatedAt: raw.updated_at,
    guestInviteToken: raw.guest_invite_token,
    isDefault: raw.is_default ?? false,
    qrCodes: (raw.qr_codes ?? []).map(VaultsSerializer.getDeSerializeQrCode),
    myAccess: mapMyAlbumAccess(raw.my_access),
    shareLinkEnabled: raw.share_link_enabled,
  });

  // Album shared with the current user: the extended-view album plus the sharer
  // (owner) info and the granted role. globalOrderIndex is unused for shared
  // albums (they aren't reorderable) so it defaults to 0.
  static deSerializeSharedAlbum = (raw: SharedAlbumRaw): SharedAlbum => ({
    ...VaultsSerializer.deSerializeAlbumDetail(raw),
    globalOrderIndex: 0,
    vaultId: raw.vault_id,
    accessLevel: raw.access_level ?? null,
    sharedBy: {
      id: raw.shared_by?.id,
      name: raw.shared_by?.name,
      email: raw.shared_by?.email,
    },
  });

  static deSerializeVaults(
    response: VaultsPaginationRaw,
  ): VaultsPaginationSerialized {
    return {
      data: response.data.map((vault: VaultRaw) =>
        VaultsSerializer.deSerializeVault(vault),
      ),
      pagination: PaginationSerializer.deSerializePagination(
        response.pagination,
      ),
    };
  }

  static deSerializeContributorInCallResponse(
    data: ContributorInCallRaw,
  ): ContributorInCallSerialized {
    return {
      id: data.id,
      name: data.name,
    };
  }

  static deSerializeReminder(data: ReminderRaw): ReminderType {
    return {
      id: data.id,
      frequency: data.frequency,
      targetId: data.target_id,
      lastEditorId: data.last_editor_id,
      targetIsOwner: data.target_is_owner,
    };
  }

  static getDeSerializeQrCode = (qrCode: QrCodeRaw): QrCode => ({
    id: qrCode.id,
    code: qrCode.code,
    vaultId: qrCode.vault_id,
    clipId: qrCode.clip_id,
    albumId: qrCode.album_id ?? undefined,
    contentId: qrCode.content_id ?? undefined,
    owner: qrCode.owner,
    linkDate: qrCode.link_date ?? undefined,
    image: qrCode.image_url ?? undefined,
  });

  static deSerializeVault(vault: VaultRaw): VaultSerialized {
    const answeredAlbums: Album[] = [];
    const unansweredAlbums: Album[] = [];
    const allAlbums: Album[] = [];
    // The renamed backend returns `albums`; the legacy/QA backend (pre
    // story→album rename) returns the same shape under `memories`.
    const rawAlbums = vault.albums ?? vault.memories;
    if (rawAlbums) {
      rawAlbums.forEach((albumRaw, index) => {
        const albumSerialized = this.deSerializeAlbum(albumRaw, index);
        allAlbums.push(albumSerialized);
        const hasContent = memoriesOf(albumSerialized).length > 0;
        if (hasContent) {
          answeredAlbums.push(albumSerialized);
        } else {
          unansweredAlbums.push(albumSerialized);
        }
      });
    }
    const deSerializeClips = (clips: ClipRaw[]): ClipSerialized[] =>
      clips.map((clip: ClipRaw) => VaultsSerializer.deSerializeClip(clip));

    const getDeSeralizeUnansweredQuestions = (
      clips: ClipRaw[],
    ): ClipSerialized[] => {
      const unansweredQuestions = clips.filter(
        (clip: ClipRaw) => !clip.video_url!,
      );

      return deSerializeClips(unansweredQuestions);
    };

    const getDeSeralizeAnswerQuestions = (
      clips: ClipRaw[],
    ): ClipSerialized[] => {
      const answeredQuestions = clips.filter((clip: ClipRaw) => clip.video_url);

      return deSerializeClips(answeredQuestions);
    };

    const getDeSerializeAgoraVideoCallProps = (
      videoCallprops: AgoraVideoCallPropsRaw,
    ): AgoraVideoCallPropsSerialized => ({
      maxCallTime: videoCallprops.max_call_time,
      usedCallTime: videoCallprops.used_call_time,
      ownerName: videoCallprops.owner_name,
      videoCallToken: videoCallprops.video_call_token,
      videocallInterviewerId: videoCallprops.videocall_interviewer_id,
      videocallRespondentId: videoCallprops.videocall_respondent_id,
      videocallInterviewerIsGuest:
        videoCallprops.videocall_interviewer_is_guest,
      videocallRespondentIsGuest: videoCallprops.videocall_respondent_is_guest,
    });

    return {
      id: vault.id,
      title: vault.title,
      coverUrl: vault.cover_url,
      coverThumbnailUrl: vault.cover_thumbnail_url,
      clipAmount: vault.clip_amount,
      createdAt: vault.created_at,
      lastUpdate: vault.last_update,
      totalTime: vault.total_time,
      unansweredQuestions: vault.clips
        ? getDeSeralizeUnansweredQuestions(vault.clips)
        : [],
      clips: vault.clips ? getDeSeralizeAnswerQuestions(vault.clips) : [],
      scheduledDowngrade: vault.scheduled_downgrade,
      subscriptionStatus: vault.subscription_status,
      subscriptionType: vault.subscription_type,
      owner: vault.owner
        ? OwnerSerializer.deSerializeOwnerInfo(vault.owner)
        : new Owner(),
      contributors: vault.contributors
        ? vault.contributors.map((contri) =>
            this.deSerializeContributorInfo(contri),
          )
        : [],
      lastPayment: vault.last_payment
        ? PaymentSerializer.deSerializePaymentInfo(vault.last_payment)
        : null,
      lastPendingPayment: vault.last_pending_payment
        ? PaymentSerializer.deSerializePaymentInfo(vault.last_pending_payment)
        : null,
      nextPaymentDate: vault.next_payment_date ? vault.next_payment_date : null,
      nextPaymentAmount: vault.next_payment_amount,
      videoCallProps: vault.video_call_props
        ? getDeSerializeAgoraVideoCallProps(vault.video_call_props)
        : new AgoraVideoCallProps(),
      qrCodes: vault.qr_codes
        ? vault.qr_codes.map(this.getDeSerializeQrCode)
        : [],
      reminder: vault.reminder
        ? this.deSerializeReminder(vault.reminder)
        : undefined,
      maxClips: vault.max_clips,
      guests: vault.guests
        ? vault.guests.map((guest) => this.deSerializeGuestInfo(guest))
        : [],
      contributorTotal: vault.contributor_total,
      guestTotal: vault.guest_total,
      albums: answeredAlbums,
      unansweredAlbums,
      guestInviteToken: vault.guest_invite_token,
      myAccess: mapMyVaultAccess(vault.my_access),
      allAlbums,
      planKey: vault.plan_key ?? null,
      storageKind: vault.storage_kind ?? null,
      storageLimit: vault.storage_limit ?? 0,
      aiCredits: vault.ai_credits ?? 0,
      totalStorage: vault.total_storage ?? 0,
      memoryOrder: vault.memory_order ?? [],
    };
  }

  static deSerializeStoragePlan(plan: StoragePlanRaw): StoragePlanType {
    return {
      id: plan.id,
      name: plan.name,
      kind: plan.kind,
      storageGb: plan.storage_gb,
      storageBytes: plan.storage_bytes,
      price: plan.price,
      aiCredits: plan.ai_credits,
      mediaEstimate: plan.media_estimate ?? undefined,
      stripePriceId: plan.stripe_price_id ?? undefined,
      currency: plan.currency ?? 'usd',
      interval: plan.interval ?? undefined,
      priceType: plan.price_type ?? undefined,
      purchasable: plan.purchasable ?? false,
    };
  }

  static serializeVaultsQueryParams(
    params: VaultsQueryParamsSerialized,
  ): VaultsQueryParamsRaw {
    return {
      search: params.search,
      sort_by: params.sortBy,
      page_num: params.pageNum,
      page_size: params.pageSize,
      role: params.role,
      active: params.active,
      has_qr: params.hasQr,
      locked: params.locked,
      restricted: params.restricted,
    };
  }

  static deSerializeClip(clip: ClipRaw): ClipSerialized {
    return {
      id: clip.id,
      contentId: clip.content?.id,
      createdAt: clip.created_at,
      body: clip.body,
      createdBy: clip.created_by,
      recordedBy: clip.recorded_by,
      recordedByOwner: clip.created_by_owner,
      createdByOwner: clip.created_by_owner,
      description: clip.description,
      isPrivate: clip.is_private,
      length: clip.length,
      name: clip.name,
      order: clip.order,
      recordedAt: clip.recorded_at,
      thumbnailUrl: clip.thumbnail_url,
      videoUrl: clip.video_url,
      recordingStatus: clip.recording_status,
      qrCodes: clip.qr_codes
        ? clip.qr_codes.map(this.getDeSerializeQrCode)
        : [],
      isAudioOnly: clip.is_audio_only,
      customThumbnail: clip.custom_thumbnail_url,
      contentType: clip.content_type,
    };
  }

  static serializeNewVault(newVault: VaultNewRequestType): VaultNewRequestRaw {
    return {
      vault: {
        title: newVault.title,
        cover_signed_id: newVault.coverSignedId,
        subscription_type: newVault.subscriptionType,
        plan_key: newVault.planKey,
        // The vault invitation role: a contributor who can't see the vault
        // is a limited contributor.
        contributors: newVault.contributors.map((contributor) => ({
          name: contributor.name,
          email: contributor.email,
          role: vaultInvitationRole(contributor),
        })),
        note: newVault.note || '',
      },
    };
  }

  static serializeEditQuestion(
    question: QuestionEditRequestType,
  ): QuestionEditRequestRaw {
    return {
      id: question.id,
      name: question.name,
      description: question.description,
      is_private: question.isPrivate,
    };
  }

  static serializeVaultEditRequest(
    data: VaultEditRequestType,
  ): VaultEditRequestRaw {
    const { questions } = data.vault;

    return {
      vault: {
        id: data.vault.id,
        title: data.vault.title,
        cover_signed_id: data.vault.coverSignedId,
        questions:
          questions && questions.map((q) => this.serializeEditQuestion(q)),
        template_ids: data.vault.templateIds,
      },
    };
  }

  static serializeClipEditRequest(
    clip: ClipEditRequestType,
  ): ClipEditRequestRaw {
    return {
      clip: {
        name: clip.name,
        description: clip.description,
        is_private: clip.isPrivate,
        video_signed_id: clip.videoSignedId,
        is_audio_only: clip.isAudioOnly,
        custom_thumbnail_signed_id: clip.customThumbnailSignedId,
        recorded_at: clip.recodedAt,
        batch_id: clip.batchId,
        position_in_batch: clip.positionInBatch,
      },
    };
  }

  static serializeDestroyClipsRequest(
    data: DestroyClipsRequestType,
  ): DestroyClipsRequestRaw {
    return {
      clip_ids: data.clipIds,
    };
  }

  static serializeCreateClipRequest(
    data: CreateClipRequestType,
  ): CreateClipRequestRaw {
    return {
      clip: {
        video_signed_id: data.clip.videoSignedId,
        name: data.clip.name,
        recorded_at: data.clip.recordedAt,
        description: data.clip.description,
        is_audio_only: data.clip.isAudioOnly,
        custom_thumbnail_signed_id: data.clip.customThumbnailSignedId,
        batch_id: data.clip.batchId,
        position_in_batch: data.clip.positionInBatch,
        // 0 is the modal's "no album"; Ruby reads 0 as present, so omit it.
        album_id: data.clip.albumId || undefined,
      },
    };
  }

  static serializeCreateDocumentRequest(
    data: CreateClipRequestType,
    vaultId: number,
  ): CreateDocumentRequestRaw {
    return {
      document: {
        vault_id: vaultId,
        file_name: data.clip.name,
        private: data.clip.isPrivate || false,
        // A document always carries a file; only prompts (unrecorded clips)
        // omit the signed id.
        signed_blob_id: data.clip.videoSignedId!,
        // 0 is the modal's "no album"; Ruby reads 0 as present, so omit it.
        album_id: data.clip.albumId || undefined,
        start_date: data.clip.startDate,
        end_date: data.clip.endDate,
        location: data.clip.location,
      },
    };
  }

  static deSerializeGuest(data: RecordingGuestRaw): RecordingGuestType {
    return {
      id: data.id,
      vault_id: data.vault_id,
      vault_title: data.vault_title,
      email: data.email,
      name: data.name,
      expiration_date: data.expiration_date,
      token: data.token,
      vault_url: data.vault_url,
    };
  }

  static serializeReminderRequest(
    data: ReminderRequestType,
  ): ReminderRequestRaw {
    return {
      reminder: {
        target_id: data.targetId,
        frequency: data.frequency,
        target_is_owner: data.targetIsOwner,
      },
    };
  }

  static serializeBuyExtraCallTime(
    extra_call_time: number,
  ): BuyExtraCallTimeRaw {
    return {
      vault: {
        hours_amount: extra_call_time,
      },
    };
  }

  static serializeCollaboratorsQueryParams(
    params: CollaboratorsQueryParamsSerialized,
  ): CollaboratorsQueryParamsRaw {
    return {
      search: params.search,
      sort_by: params.sortBy?.id as string,
      page_num: params.pageNum,
      page_size: params.pageSize,
      access_level: params.accessLevel,
      has_contributed: params.collaboratorActivity,
      collaborator_role: params.collaboratorRole,
      collaborator_status: params.collaboratorStatus,
      vault_status: params.vaultStatus,
      vault_type: params.vaultType,
      invite_origin: params.inviteOrigin,
      format: params.format,
      contributors: params.contributors,
      guests: params.guests,
      focus_vault_id: params.focusVaultId,
    };
  }

  static deSerializeOwnedInvites(data: OwnedInvitesRaw): OwnedInvitesType {
    return {
      totalInvites: data.total_invites,
      totalPendingInvitesAllVaults: data.total_invites_all_vaults,
      breakdown: {
        guestTokens: data.breakdown.guest_tokens,
        vaultInvitations: data.breakdown.vault_invitations,
      },
    };
  }

  static serializeAuditDetailQueryParams(
    params: CollaboratorAuditDetailQueryParams,
  ): CollaboratorAuditDetailQueryParams {
    return {
      vault_id: params.vault_id,
      collaborator_email: encodeURI(params.collaborator_email),
    };
  }

  static deserializeCollaboratorAuditDetail(
    data: CollaboratorAuditDetailRaw,
  ): CollaboratorAuditDetailType {
    return {
      currentVault: {
        coverUrl: data.current.vault_info.cover_url,
        subscriptionType: data.current.vault_info.subscription_type,
        ...data.current.vault_info,
      },
      currentType: data.current.type,
      currentSince: data.current.since,
      history: data.history.map((element: AuditDetailElement) => ({
        type: element.role,
        startDate: new Date(element.created_at),
        endDate:
          element.event !== 'added' ? new Date(element.updated_at) : undefined,
      })),
      otherVaults: data.other_vaults,
    };
  }

  static serializeAlbumMetadataRequest(
    id: number,
    data: AlbumMetadataRequestType,
  ): AlbumMetadataRequestRaw {
    const album: {
      id: number;
      start_date?: string | null;
      end_date?: string | null;
      location?: string | null;
      name?: string | null;
    } = { id };

    if (data.startDate != null) {
      album.start_date = data.startDate;
    }
    if (data.endDate != null) {
      album.end_date = data.endDate;
    }
    if (data.location != null) {
      album.location = data.location;
    }
    if (data.name != null) {
      album.name = data.name;
    }

    return {
      metadata: {
        album,
      },
    };
  }

  static serializeJournalEntryRequest(
    albumId: number | null,
    data: JournalEntryRequestType,
    vaultId?: number,
  ): JournalEntryRequestRaw {
    return {
      journal: {
        // Without an album the entry is unfiled: the API parks it in the
        // vault's hidden default album.
        ...(albumId ? { album_id: albumId } : { vault_id: vaultId }),
        name: data.name,
        body: data.body,
        entry_date: data.entryDate,
        location: data.location,
        start_date: data.startDate,
        end_date: data.endDate,
      },
    };
  }

  static serializeUpdateJournalEntryRequest(
    data: UpdateJournalEntryRequestType,
  ): UpdateJournalEntryRequestRaw {
    return {
      journal: {
        name: data.name,
        body: data.body,
        entry_date: data.entryDate,
        location: data.location,
      },
    };
  }

  static serializeContentMetadataRequest(
    id: number,
    data: ContentMetadataRequestType,
  ): ContentMetadataRequestRaw {
    const content: {
      id: number;
      name?: string;
      start_date?: string | null;
      end_date?: string | null;
      description?: string;
      location?: string;
      album_id?: number;
      is_private?: boolean;
      custom_thumbnail_signed_id?: string;
    } = { id };

    if (data.name != null) {
      content.name = data.name;
    }
    if (data.isPrivate != null) {
      content.is_private = data.isPrivate;
    }
    if (data.albumId != null) {
      content.album_id = data.albumId;
    }
    if (data.startDate != null) {
      content.start_date = data.startDate;
    }
    if (data.endDate != null) {
      content.end_date = data.endDate;
    }
    if (data.description != null) {
      content.description = data.description;
    }
    if (data.location != null) {
      content.location = data.location;
    }
    // A signed blob id sets a video's thumbnail; null removes it.
    if (data.customThumbnailSignedId !== undefined) {
      content.custom_thumbnail_signed_id = data.customThumbnailSignedId ?? '';
    }

    return {
      content,
    };
  }

  static deSerializeAlbumLocations(data: AlbumLocationRaw): string[] {
    return data.locations.map((location) => location.location);
  }

  static serializeContentOrderRequest(
    data: ContentOrderRequestType,
  ): ContentOrderRequestRaw {
    return {
      orders: data.map((order) => ({
        content_id: order.contentId,
        order: order.order,
      })),
    };
  }

  static serializeAlbumOrderRequest(
    data: AlbumOrderRequestType,
  ): AlbumOrderRequestRaw {
    return {
      orders: data.map((order) => ({
        album_id: order.albumId,
        order: order.order,
      })),
    };
  }
}

export { VaultsSerializer };
